import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { api, ApiError, downloadFile, mutate } from '../shared/api';
import { formatDate, formatNumber } from '../shared/formatting';

type Locale = 'hr' | 'en';

type Nacin = 'vozac' | 'hp' | 'gls';

const NACIN_LABEL: Record<Nacin, { hr: string; en: string }> = {
    vozac: { hr: 'Vozač', en: 'Driver' },
    hp: { hr: 'HP', en: 'HP' },
    gls: { hr: 'GLS', en: 'GLS' },
};

type Shipment = {
    id: number;
    warehouse_code: string;
    poslovnica: string;
    status: 'pakirano' | 'na_putu' | 'otkazano';
    nacin: Nacin | null;
    packed_at: string | null;
    sent_at: string | null;
    items: { sifra: string; artikl: string | null; vrsta: string | null; kolicina: number }[];
};

type ActionResp = {
    ok: boolean;
    created?: { id: number; warehouse_code: string }[];
    changed?: number[];
    skipped: { warehouse_code?: string; id?: number; razlog: string }[];
};

type Transfer = {
    warehouse_code: string;
    poslovnica: string;
    vrsta: string;
    sifra: string;
    artikl: string;
    kolicina: number;
    min_qty: number;
    max_qty: number;
    slobodno: number;
};

type Shortage = {
    warehouse_code: string;
    poslovnica: string;
    vrsta: string;
    min_qty: number;
    max_qty: number;
    slobodno: number;
    potrebno: number;
    poslano: number;
    nedostaje: number;
};

type Resp = {
    as_of: { zaliha_batch: number | null; zaliha_datum: string | null; skladiste: string; poslovnica: number; pripremljeno_kom: number };
    transfers: Transfer[];
    shortages: Shortage[];
    source_left: { sifra: string; artikl: string; kolicina: number }[];
};

/**
 * Modul 40.44, Dopuna lagera, priprema (mail UID 990, Ruzica Sarajlic).
 * Prijedlog preskladistenja sa 214 CENTRALNO POSLOVNICE u poslovnice po asortimanu
 * (na MIN ili ispod -> do MAX), sifre unutar vrste redom, i Excel. Odabrane poslovnice
 * postaju dopuna u statusu pakirano, a zatim poslano uz nacin slanja (vozac, HP, GLS).
 */
export function BranchRestockModule({ locale, token, onBack }: { locale: Locale; token: string | null; onBack?: () => void }) {
    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);

    const [data, setData] = useState<Resp | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [branch, setBranch] = useState('');
    const [shipments, setShipments] = useState<Shipment[]>([]);
    const [pickBranches, setPickBranches] = useState<Set<string>>(new Set());
    const [pickShip, setPickShip] = useState<Set<number>>(new Set());
    const [nacin, setNacin] = useState<Nacin | ''>('');
    const [busy, setBusy] = useState(false);
    const [notice, setNotice] = useState<string | null>(null);

    const load = useCallback(() => {
        setLoading(true);
        setError(null);
        Promise.all([api<Resp>('/branch-restock', token), api<{ shipments: Shipment[] }>('/branch-restock/shipments', token)])
            .then(([r, s]) => {
                setData(r);
                setShipments(s.shipments);
                setPickBranches(new Set());
                setPickShip(new Set());
            })
            .catch((e) => setError(e instanceof ApiError ? e.message : t('Učitavanje nije uspjelo.', 'Loading failed.')))
            .finally(() => setLoading(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token, locale]);

    useEffect(() => {
        load();
    }, [load]);

    const branches = useMemo(() => {
        const set = new Set<string>();
        (data?.transfers ?? []).forEach((r) => set.add(r.poslovnica));
        (data?.shortages ?? []).forEach((r) => set.add(r.poslovnica));
        return Array.from(set).sort((a, b) => a.localeCompare(b, 'hr'));
    }, [data]);

    const transfers = useMemo(() => (data?.transfers ?? []).filter((r) => !branch || r.poslovnica === branch), [data, branch]);
    const shortages = useMemo(() => (data?.shortages ?? []).filter((r) => !branch || r.poslovnica === branch), [data, branch]);
    const pieces = transfers.reduce((s, r) => s + r.kolicina, 0);
    const branchCount = new Set(transfers.map((r) => r.poslovnica)).size;

    const transferBranches = useMemo(() => {
        const m = new Map<string, { code: string; name: string; kom: number; rows: number }>();
        transfers.forEach((r) => {
            const e = m.get(r.warehouse_code) ?? { code: r.warehouse_code, name: r.poslovnica, kom: 0, rows: 0 };
            e.kom += r.kolicina;
            e.rows += 1;
            m.set(r.warehouse_code, e);
        });
        return Array.from(m.values());
    }, [transfers]);

    const packed = shipments.filter((s) => s.status === 'pakirano' && (!branch || s.poslovnica === branch));
    const done = shipments.filter((s) => s.status !== 'pakirano' && (!branch || s.poslovnica === branch));

    const toggleSet = <T,>(set: Set<T>, v: T) => {
        const next = new Set(set);
        if (next.has(v)) next.delete(v);
        else next.add(v);
        return next;
    };

    const act = async (path: string, body: Record<string, unknown>, okText: (r: ActionResp) => string) => {
        setBusy(true);
        setError(null);
        setNotice(null);
        try {
            const r = await mutate<ActionResp>(path, token, 'POST', body);
            const skipped = r.skipped.map((s) => s.razlog).filter((v, i, a) => a.indexOf(v) === i);
            setNotice(okText(r) + (skipped.length ? ` ${t('Preskočeno', 'Skipped')} ${r.skipped.length} (${skipped.join(' ')})` : ''));
            load();
        } catch (e) {
            setError(e instanceof ApiError ? e.message : t('Spremanje nije uspjelo.', 'Saving failed.'));
        } finally {
            setBusy(false);
        }
    };

    const packSelected = () =>
        act('/branch-restock/pack', { warehouses: Array.from(pickBranches) }, (r) => t(`Pakirano za ${r.created?.length ?? 0} poslovnica.`, `Packed for ${r.created?.length ?? 0} branches.`));
    const shipSelected = () =>
        act('/branch-restock/ship', { ids: Array.from(pickShip), nacin }, (r) => t(`Poslano ${r.changed?.length ?? 0}.`, `Sent ${r.changed?.length ?? 0}.`));
    const cancelSelected = () =>
        act('/branch-restock/cancel', { ids: Array.from(pickShip) }, (r) => t(`Otkazano ${r.changed?.length ?? 0}.`, `Cancelled ${r.changed?.length ?? 0}.`));

    const exportXlsx = async () => {
        try {
            const qs = branch ? `?branch=${encodeURIComponent(branch)}` : '';
            await downloadFile(`/branch-restock/export.xlsx${qs}`, token, `dopuna-lagera-priprema-${new Date().toISOString().slice(0, 10)}.xlsx`);
        } catch {
            setError(t('Izvoz nije uspio.', 'Export failed.'));
        }
    };

    const th: React.CSSProperties = { textAlign: 'left', padding: '0.35rem 0.5rem', borderBottom: '1px solid #999', whiteSpace: 'nowrap' };
    const td: React.CSSProperties = { padding: '0.3rem 0.5rem', borderBottom: '1px solid #ddd', verticalAlign: 'top' };
    const num: React.CSSProperties = { ...td, textAlign: 'right', whiteSpace: 'nowrap' };
    const thn: React.CSSProperties = { ...th, textAlign: 'right' };

    const renderShipments = (rows: Shipment[], selectable: boolean) => (
        <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.88rem' }}>
                <thead>
                    <tr>
                        {selectable ? (
                            <th style={th}>
                                <input
                                    type="checkbox"
                                    aria-label={t('Označi sve', 'Select all')}
                                    checked={rows.every((r) => pickShip.has(r.id))}
                                    onChange={() => setPickShip(rows.every((r) => pickShip.has(r.id)) ? new Set() : new Set(rows.map((r) => r.id)))}
                                />
                            </th>
                        ) : null}
                        <th style={th}>{t('Poslovnica', 'Branch')}</th>
                        <th style={th}>{t('Status', 'Status')}</th>
                        <th style={th}>{t('Pakirano', 'Packed')}</th>
                        <th style={th}>{t('Poslano', 'Sent')}</th>
                        <th style={th}>{t('Artikli', 'Items')}</th>
                        <th style={thn}>{t('Kom', 'Pcs')}</th>
                    </tr>
                </thead>
                <tbody>
                    {rows.map((s) => (
                        <tr key={s.id}>
                            {selectable ? (
                                <td style={td}>
                                    <input type="checkbox" checked={pickShip.has(s.id)} onChange={() => setPickShip((p) => toggleSet(p, s.id))} />
                                </td>
                            ) : null}
                            <td style={td}>{s.poslovnica}</td>
                            <td style={td}>
                                {s.status === 'pakirano' ? t('Pakirano', 'Packed') : s.status === 'na_putu' ? `${t('Poslano', 'Sent')}${s.nacin ? `, ${NACIN_LABEL[s.nacin][locale]}` : ''}` : t('Otkazano', 'Cancelled')}
                            </td>
                            <td style={td}>{s.packed_at ? formatDate(s.packed_at, locale) : ''}</td>
                            <td style={td}>{s.sent_at ? formatDate(s.sent_at, locale) : ''}</td>
                            <td style={td}>{s.items.map((i) => `${i.sifra} x ${formatNumber(i.kolicina, locale)}`).join(', ')}</td>
                            <td style={num}>{formatNumber(s.items.reduce((a, i) => a + i.kolicina, 0), locale)}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );

    return (
        <div className="module-shell">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: '1rem', flexWrap: 'wrap' }}>
                <div>
                    {onBack ? (
                        <button type="button" className="secondary compact" onClick={onBack}>
                            ← {t('Skladište', 'Warehouse')}
                        </button>
                    ) : null}
                    <p className="eyebrow">40.44</p>
                    <h2 style={{ margin: '0.2rem 0' }}>{t('Dopuna lagera, priprema', 'Branch stock replenishment')}</h2>
                    <p className="muted" style={{ margin: 0 }}>
                        {t(
                            'Poslovnica kojoj je roba za slobodnu prodaju (zaliha + na putu - narudžba) na MIN-u asortimana ili ispod dobiva dopunu do MAX-a sa CENTRALNO POSLOVNICE (214). Šifre iste vrste troše se redom, a kad 214 nema dovoljno, prednost ima najpraznija poslovnica.',
                            'A branch whose free stock (stock + in transit - ordered) is at or below the assortment MIN is topped up to MAX from warehouse 214. Codes of the same kind are used in order; when 214 is short, the emptiest branch goes first.',
                        )}
                    </p>
                    {data ? (
                        <p className="muted" style={{ margin: '0.2rem 0 0', fontSize: '0.82rem' }}>
                            {t('Zaliha na dan', 'Stock as of')} {data.as_of.zaliha_datum ? formatDate(data.as_of.zaliha_datum, locale) : '-'} ·{' '}
                            {t(`${data.as_of.poslovnica} poslovnica u asortimanu`, `${data.as_of.poslovnica} branches in assortment`)}
                        </p>
                    ) : null}
                </div>
                <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                    <select value={branch} onChange={(e) => setBranch(e.target.value)} aria-label={t('Poslovnica', 'Branch')}>
                        <option value="">{t('Sve poslovnice', 'All branches')}</option>
                        {branches.map((b) => (
                            <option key={b} value={b}>
                                {b}
                            </option>
                        ))}
                    </select>
                    <button type="button" className="secondary compact" onClick={load} disabled={loading}>
                        {t('Osvježi', 'Refresh')}
                    </button>
                    <button type="button" className="secondary compact" onClick={exportXlsx} disabled={!data}>
                        {t('Izvoz u Excel', 'Export to Excel')}
                    </button>
                </div>
            </div>

            {error ? (
                <p role="alert" style={{ color: '#a00', marginTop: '0.8rem' }}>
                    {error}
                </p>
            ) : null}
            {notice ? (
                <p role="status" style={{ border: '1px solid #999', padding: '0.5rem 0.8rem', marginTop: '0.8rem' }}>
                    {notice}
                </p>
            ) : null}
            {loading && !data ? <p className="muted">{t('Učitavam...', 'Loading...')}</p> : null}

            {data ? (
                <>
                    <section style={{ marginTop: '1.2rem' }}>
                        <h3 style={{ margin: '0 0 0.4rem' }}>
                            {t('Preskladištenje', 'Transfers')} ({t(`${branchCount} poslovnica, ${formatNumber(pieces, locale)} kom`, `${branchCount} branches, ${formatNumber(pieces, locale)} pcs`)})
                        </h3>
                        {transferBranches.length > 0 ? (
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.3rem 1rem', alignItems: 'center', margin: '0 0 0.6rem' }}>
                                <label>
                                    <input
                                        type="checkbox"
                                        checked={transferBranches.every((b) => pickBranches.has(b.code))}
                                        onChange={() =>
                                            setPickBranches(transferBranches.every((b) => pickBranches.has(b.code)) ? new Set() : new Set(transferBranches.map((b) => b.code)))
                                        }
                                    />{' '}
                                    {t('Sve', 'All')}
                                </label>
                                {transferBranches.map((b) => (
                                    <label key={b.code}>
                                        <input type="checkbox" checked={pickBranches.has(b.code)} onChange={() => setPickBranches((p) => toggleSet(p, b.code))} /> {b.name} ({formatNumber(b.kom, locale)} {t('kom', 'pcs')})
                                    </label>
                                ))}
                                <button type="button" className="compact" disabled={busy || pickBranches.size === 0} onClick={packSelected}>
                                    {t(`Označi pakirano (${pickBranches.size})`, `Mark packed (${pickBranches.size})`)}
                                </button>
                            </div>
                        ) : null}
                        {transfers.length === 0 ? (
                            <p className="muted">{t('Nema ništa za poslati sa 214.', 'Nothing to send from 214.')}</p>
                        ) : (
                            <div style={{ overflowX: 'auto' }}>
                                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.88rem' }}>
                                    <thead>
                                        <tr>
                                            <th style={th}>{t('Poslovnica', 'Branch')}</th>
                                            <th style={th}>{t('Vrsta artikla', 'Kind')}</th>
                                            <th style={th}>{t('Šifra', 'Code')}</th>
                                            <th style={th}>{t('Artikal', 'Item')}</th>
                                            <th style={thn}>{t('Količina', 'Qty')}</th>
                                            <th style={thn}>{t('Slobodno', 'Free')}</th>
                                            <th style={thn}>MIN</th>
                                            <th style={thn}>MAX</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {transfers.map((r, i) => (
                                            <tr key={`${r.warehouse_code}-${r.sifra}-${i}`}>
                                                <td style={td}>{r.poslovnica}</td>
                                                <td style={td}>{r.vrsta}</td>
                                                <td style={td}>{r.sifra}</td>
                                                <td style={td}>{r.artikl}</td>
                                                <td style={num}>{formatNumber(r.kolicina, locale)}</td>
                                                <td style={num}>{formatNumber(r.slobodno, locale)}</td>
                                                <td style={num}>{formatNumber(r.min_qty, locale)}</td>
                                                <td style={num}>{formatNumber(r.max_qty, locale)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </section>

                    <section style={{ marginTop: '1.2rem' }}>
                        <h3 style={{ margin: '0 0 0.4rem' }}>
                            {t('Nedostaje na 214', 'Short on 214')} ({shortages.length})
                        </h3>
                        {shortages.length === 0 ? (
                            <p className="muted">{t('Sve potrebe pokrivene su sa 214.', 'All needs are covered from 214.')}</p>
                        ) : (
                            <div style={{ overflowX: 'auto' }}>
                                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.88rem' }}>
                                    <thead>
                                        <tr>
                                            <th style={th}>{t('Poslovnica', 'Branch')}</th>
                                            <th style={th}>{t('Vrsta artikla', 'Kind')}</th>
                                            <th style={thn}>{t('Slobodno', 'Free')}</th>
                                            <th style={thn}>MIN</th>
                                            <th style={thn}>MAX</th>
                                            <th style={thn}>{t('Potrebno', 'Needed')}</th>
                                            <th style={thn}>{t('Ide iz 214', 'From 214')}</th>
                                            <th style={thn}>{t('Nedostaje', 'Short')}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {shortages.map((r, i) => (
                                            <tr key={`${r.warehouse_code}-${r.vrsta}-${i}`}>
                                                <td style={td}>{r.poslovnica}</td>
                                                <td style={td}>{r.vrsta}</td>
                                                <td style={num}>{formatNumber(r.slobodno, locale)}</td>
                                                <td style={num}>{formatNumber(r.min_qty, locale)}</td>
                                                <td style={num}>{formatNumber(r.max_qty, locale)}</td>
                                                <td style={num}>{formatNumber(r.potrebno, locale)}</td>
                                                <td style={num}>{formatNumber(r.poslano, locale)}</td>
                                                <td style={num}>{formatNumber(r.nedostaje, locale)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </section>

                    <section style={{ marginTop: '1.2rem' }}>
                        <h3 style={{ margin: '0 0 0.4rem' }}>
                            {t('Pakirano', 'Packed')} ({packed.length})
                        </h3>
                        {packed.length === 0 ? (
                            <p className="muted">{t('Nema pakiranih dopuna.', 'No packed replenishments.')}</p>
                        ) : (
                            <>
                                <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap', margin: '0 0 0.5rem' }}>
                                    <select value={nacin} onChange={(e) => setNacin(e.target.value as Nacin | '')} aria-label={t('Način slanja', 'Carrier')}>
                                        <option value="">{t('Način slanja', 'Carrier')}</option>
                                        {(Object.keys(NACIN_LABEL) as Nacin[]).map((n) => (
                                            <option key={n} value={n}>
                                                {NACIN_LABEL[n][locale]}
                                            </option>
                                        ))}
                                    </select>
                                    <button type="button" className="compact" disabled={busy || pickShip.size === 0 || !nacin} onClick={shipSelected}>
                                        {t(`Označi poslano (${pickShip.size})`, `Mark sent (${pickShip.size})`)}
                                    </button>
                                    <button type="button" className="secondary compact" disabled={busy || pickShip.size === 0} onClick={cancelSelected}>
                                        {t('Otkaži pakirano', 'Cancel packed')}
                                    </button>
                                </div>
                                {renderShipments(packed, true)}
                            </>
                        )}
                    </section>

                    {done.length > 0 ? (
                        <section style={{ marginTop: '1.2rem' }}>
                            <h3 style={{ margin: '0 0 0.4rem' }}>
                                {t('Poslano i otkazano, zadnjih 30 dana', 'Sent and cancelled, last 30 days')} ({done.length})
                            </h3>
                            {renderShipments(done, false)}
                        </section>
                    ) : null}
                </>
            ) : null}
        </div>
    );
}
